import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, throwError, from, of } from 'rxjs';
import { catchError, switchMap, take, map } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { DeviceService } from '../services/device.service';
import { DiagnosticsSessionService } from '../services/diagnostics-session.service';
import { AuthSession } from '../models/auth.models';
import { LOGOUT_DISPATCH_ALLOWED } from '../utils/logout-request-context';

/**
 * Adds auth headers and recovers from expiring/expired access tokens by
 * waiting on AuthService.refreshToken() and retrying once.
 *
 * The interceptor never ends the session: AuthService.refreshToken() owns that
 * decision (inactivity sign-out, logout, or keeping the session on a network
 * error) and shares one in-flight refresh between all callers (AB#20064).
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private deviceService: DeviceService,
    private diagnosticsSessionService: DiagnosticsSessionService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Settings must load independently of the session whose refresh needs them.
    // Resolve against the base href so nested portal installations work too.
    if (this.isRuntimeConfigRequest(req)) {
      return next.handle(req);
    }

    // Skip auth header for external services (like Azure blob storage)
    if (this.isExternalService(req.url)) {
      return next.handle(req);
    }

    // Skip interceptor for auth endpoints to avoid circular dependencies
    if (this.isAuthEndpoint(req.url)) {
      return from(this.addInstallationId(req)).pipe(
        switchMap(modifiedReq => next.handle(modifiedReq))
      );
    }

    // Logout is sent as-is: AuthService.logout() already refreshes an expired
    // access token once before revoking; refreshing here as well would rotate
    // (or re-send) the refresh token of a session that is being ended.
    if (this.isNoRefreshEndpoint(req.url)) {
      return this.addAuthHeaders(req).pipe(
        switchMap(modifiedReq => {
          // Metadata may have awaited device storage while a new session began.
          // Check the original logout's identity at the last forwarding point.
          const dispatchAllowed = modifiedReq.context.get(LOGOUT_DISPATCH_ALLOWED);
          return dispatchAllowed
            ? dispatchAllowed().pipe(
              switchMap(allowed => allowed ? next.handle(modifiedReq) : EMPTY)
            )
            : next.handle(modifiedReq);
        })
      );
    }

    // Token is about to expire: wait on the shared refresh, then send.
    if (this.authService.needsTokenRefresh()) {
      return this.refreshThenSend(req, next);
    }

    return this.send(req, next).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401) {
          return throwError(() => error);
        }

        if (this.authService.isInactivityTimeoutError(error)) {
          this.authService.triggerInactivityTimeout('api_401');
          return throwError(() => error);
        }

        if (!this.authService.getAuthToken()) {
          return throwError(() => error);
        }

        return this.refreshThenSend(req, next, error);
      })
    );
  }

  private send(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.addAuthHeaders(req).pipe(
      switchMap(modifiedReq => next.handle(modifiedReq))
    );
  }

  /**
   * Waits on the shared refresh, then retries with headers rebuilt from the
   * current session (which also covers an adopted rotation-race pair). On
   * refresh failure every waiter errors; the session outcome is AuthService's.
   *
   * Exception: a proactive refresh (no `originalError`) that hits a network or
   * server error still sends the request while the current access token has
   * not expired, since the refresh window opens 5 minutes before expiry.
   */
  private refreshThenSend(
    req: HttpRequest<any>,
    next: HttpHandler,
    originalError?: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    return this.authService.refreshToken().pipe(
      take(1),
      catchError(err => {
        if (!originalError && this.isTransientError(err) && this.authService.hasUnexpiredAccessToken()) {
          return of(null);
        }
        return throwError(() => originalError ?? err);
      }),
      switchMap(() => this.send(req, next))
    );
  }

  /** Offline / transport (status 0) or server (5xx) error. */
  private isTransientError(error: unknown): boolean {
    const status = (error as { status?: unknown } | null)?.status;
    return typeof status === 'number' && (status === 0 || status >= 500);
  }

  private addAuthHeaders(req: HttpRequest<any>): Observable<HttpRequest<any>> {
    const installationId = this.deviceService.getInstallationId();

    return this.authService.authSession$.pipe(
      take(1),
      switchMap((session: AuthSession) =>
        from(installationId).pipe(
          take(1),
          map((id: string) => ({
            token: session ? session.token : null,
            installationId: id,
          }))
        )
      ),
      map(({ token, installationId }: { token: string | null; installationId: string }) => {
        let headers = req.headers;

        if (installationId) {
          headers = headers.set('InstallationId', installationId);
        }

        if (token && !this.isAuthEndpoint(req.url)
          && !(this.isNoRefreshEndpoint(req.url) && headers.has('Authorization'))) {
          headers = headers.set('Authorization', `Bearer ${token}`);
        }

        const finalReq = this.applyDiagnosticHeader(req.clone({ headers }));
        return finalReq;
      })
    );
  }

  private async addInstallationId(req: HttpRequest<any>): Promise<HttpRequest<any>> {
      const installationId = await this.deviceService.getInstallationId();
    
    if (installationId) {
      return this.applyDiagnosticHeader(req.clone({
        headers: req.headers.set('InstallationId', installationId)
      }));
    }
    
    return this.applyDiagnosticHeader(req);
  }

  private applyDiagnosticHeader(request: HttpRequest<any>): HttpRequest<any> {
    const sessionId = this.diagnosticsSessionService.activeSessionId;
    if (!sessionId) {
      return request;
    }

    return request.clone({
      headers: request.headers.set(
        this.diagnosticsSessionService.headerName,
        sessionId)
    });
  }

  private isAuthEndpoint(url: string): boolean {
    const authEndpoints = [
      '/v1/auth/requests',
      '/request-email-otp',
      '/login',
      '/auth/refresh',
      '/device/register',
      '/impersonate/exchange',
      '/auth/passkeys/status',
      '/auth/passkeys/login/options',
      '/auth/passkeys/login/verify'
    ];
    
    return authEndpoints.some(endpoint => url.endsWith(endpoint));
  }

  private isNoRefreshEndpoint(url: string): boolean {
    return url.endsWith('/auth/logout');
  }

  private isRuntimeConfigRequest(req: HttpRequest<any>): boolean {
    if (req.method !== 'GET') {
      return false;
    }
    try {
      const configUrl = new URL('./assets/config/app-config.json', document.baseURI);
      return new URL(req.urlWithParams, document.baseURI).href === configUrl.href;
    } catch {
      return false;
    }
  }

  private isExternalService(url: string): boolean {
    // List of external services that should not get auth headers
    const externalPatterns = [
      'blob.core.windows.net',  // Azure blob storage
      'amazonaws.com',           // AWS S3
      'googleapis.com',          // Google APIs
      'cloudfront.net'           // AWS CloudFront
    ];
    
    return externalPatterns.some(pattern => url.includes(pattern));
  }
}
